import React, { useEffect, useState, useCallback } from 'react';
import { getEmployees, createEmployee, updateEmployee, deleteEmployee, resetEmployeePassword, getAgentUpdateStatus } from '../api';
import { getInitials } from '../utils';

// Small dotted-version compare for the agent-version indicator.
function versionBehind(installed, latest) {
  if (!installed || !latest) return false;
  const pa = String(installed).split('.').map(n => parseInt(n, 10) || 0);
  const pb = String(latest).split('.').map(n => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d) return d < 0;
  }
  return false;
}

const TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Anchorage', 'Pacific/Honolulu', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Bangkok', 'Asia/Singapore', 'Asia/Tokyo',
  'Australia/Sydney', 'Pacific/Auckland',
];

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const emptyForm = { name: '', email: '', role: 'employee', department: '', timezone: 'UTC', work_days: null };

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  // Reset password modal
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState('');
  const [resetMsg, setResetMsg] = useState('');

  const [latestAgent, setLatestAgent] = useState(null);

  const load = useCallback(async () => {
    setEmployees(await getEmployees());
  }, []);

  useEffect(() => { getAgentUpdateStatus().then(s => setLatestAgent(s.latest)).catch(() => {}); }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setError(''); setShowModal(true); };
  const openEdit = (emp) => {
    setEditing(emp);
    setForm({
      name: emp.name, email: emp.email, role: emp.role,
      department: emp.department || '', timezone: emp.timezone || 'UTC',
      work_days: emp.work_days ? JSON.parse(emp.work_days) : null,
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editing) {
        await updateEmployee(editing.id, form);
      } else {
        const res = await createEmployee(form);
        if (res && res.email_sent === false) {
          const link = `${window.location.origin}/accept-invite?token=${res.invite_token}`;
          window.alert(
            `Employee created, but the invite email could NOT be sent.\n\n` +
            `Reason: ${res.email_error || 'email delivery error'}\n\n` +
            `Share this invite link with them to set their password (valid 72h):\n${link}`
          );
        }
      }
      setShowModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this employee?')) return;
    await deleteEmployee(id);
    load();
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetMsg('');
    try {
      await resetEmployeePassword(resetTarget.id, resetPw);
      setResetMsg('Password updated successfully.');
      setResetPw('');
      setTimeout(() => { setResetTarget(null); setResetMsg(''); }, 1500);
    } catch (err) {
      setResetMsg(err.response?.data?.error || 'Failed to reset password');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Employees</h1>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Employee</button>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Name</th><th>Email</th><th>Department</th><th>Timezone</th><th>Work Days</th><th>Role</th><th>App</th><th></th></tr>
            </thead>
            <tbody>
              {employees.length === 0 && <tr><td colSpan="8" className="empty">No employees</td></tr>}
              {employees.map(emp => (
                <tr key={emp.id}>
                  <td>
                    <div className="flex gap-8" style={{ alignItems: 'center' }}>
                      <div className="employee-avatar">{getInitials(emp.name)}</div>
                      <span style={{ fontWeight: 500 }}>{emp.name}</span>
                    </div>
                  </td>
                  <td className="text-muted">{emp.email}</td>
                  <td>{emp.department || '—'}</td>
                  <td className="text-muted" style={{ fontSize: '0.8rem' }}>{emp.timezone || 'UTC'}</td>
                  <td style={{ fontSize: '0.8rem' }}>
                    {emp.work_days
                      ? JSON.parse(emp.work_days).map(d => d.slice(0,3)).join(', ')
                      : <span className="text-muted">Org default</span>}
                  </td>
                  <td><span className={`badge ${emp.role === 'manager' ? 'badge-blue' : 'badge-gray'}`}>{emp.role}</span></td>
                  <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }} title={emp.agent_version_seen_at ? `Last seen ${new Date(emp.agent_version_seen_at).toLocaleString()}` : 'No desktop app activity yet'}>
                    {emp.agent_version
                      ? <span style={{ color: versionBehind(emp.agent_version, latestAgent) ? '#d97706' : '#16a34a' }}>
                          ● v{emp.agent_version}{versionBehind(emp.agent_version, latestAgent) ? ' (old)' : ''}
                        </span>
                      : <span className="text-muted">—</span>}
                  </td>
                  <td>
                    <div className="flex gap-8">
                      <button className="btn btn-outline btn-sm" onClick={() => openEdit(emp)}>Edit</button>
                      <button className="btn btn-outline btn-sm" onClick={() => { setResetTarget(emp); setResetPw(''); setResetMsg(''); }}>🔑 Reset PW</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(emp.id)}>Remove</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">{editing ? 'Edit Employee' : 'Add Employee'}</div>
            <form onSubmit={handleSubmit}>
              <div className="form-group"><label>Full Name</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required /></div>
              <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required /></div>
              <div className="grid-2">
                <div className="form-group">
                  <label>Role</label>
                  <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                    <option value="employee">Employee</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="form-group"><label>Department</label><input value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} /></div>
              </div>
              <div className="form-group">
                <label>Timezone</label>
                <select value={form.timezone} onChange={e => setForm(f => ({ ...f, timezone: e.target.value }))}>
                  {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </div>

              {!editing && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginTop: 4, fontSize: '0.85rem', color: '#166534' }}>
                  📧 An invite email will be sent to this address so they can set their own password.
                </div>
              )}

              <div className="form-group" style={{ marginTop: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Work Days</span>
                  <button type="button" style={{ fontSize: '0.75rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}
                    onClick={() => setForm(f => ({ ...f, work_days: f.work_days === null ? ['Monday','Tuesday','Wednesday','Thursday','Friday'] : null }))}>
                    {form.work_days === null ? 'Customize' : 'Use org default'}
                  </button>
                </label>
                {form.work_days === null ? (
                  <div style={{ padding: '8px 12px', background: 'var(--bg)', borderRadius: 8, fontSize: '0.8rem', color: 'var(--muted)' }}>
                    Using organisation default work days
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                    {DAYS.map(day => {
                      const checked = form.work_days.includes(day);
                      return (
                        <label key={day} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', border: `1px solid ${checked ? 'var(--primary)' : 'var(--border)'}`, borderRadius: 8, cursor: 'pointer', background: checked ? '#eef2ff' : 'transparent', fontSize: '0.8rem' }}>
                          <input type="checkbox" checked={checked} style={{ accentColor: 'var(--primary)' }}
                            onChange={() => setForm(f => {
                              const days = f.work_days.includes(day) ? f.work_days.filter(d => d !== day) : [...f.work_days, day];
                              return { ...f, work_days: days };
                            })} />
                          {day.slice(0, 3)}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {error && <div style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: 8, padding: '8px 12px', background: '#fef2f2', borderRadius: 8 }}>{error}</div>}

              <div className="flex gap-8" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Save Changes' : 'Add Employee'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetTarget && (
        <div className="modal-overlay" onClick={() => setResetTarget(null)}>
          <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="modal-title">Reset Password</div>
            <div style={{ marginBottom: 16, color: 'var(--muted)', fontSize: '0.875rem' }}>
              Set a new password for <strong>{resetTarget.name}</strong>. Share it with them securely.
            </div>
            <form onSubmit={handleResetPassword}>
              <div className="form-group">
                <label>New Password</label>
                <input type="password" value={resetPw} onChange={e => setResetPw(e.target.value)} required minLength={6} placeholder="Min 6 characters" autoFocus />
              </div>
              {resetMsg && (
                <div style={{ fontSize: '0.85rem', marginBottom: 12, padding: '8px 12px', borderRadius: 8,
                  background: resetMsg.includes('success') ? '#f0fdf4' : '#fef2f2',
                  color: resetMsg.includes('success') ? '#16a34a' : '#ef4444' }}>
                  {resetMsg}
                </div>
              )}
              <div className="flex gap-8" style={{ justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-outline" onClick={() => setResetTarget(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Reset Password</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
